import { cn, fetcher } from '@/lib/utils'
import { useCallback, useMemo } from 'react'
import { CoreMetricItem } from './CoreItem'
import { useTranslations } from 'next-intl'
import useSWR from 'swr'
import { dayjs } from '@/utils/dayjs'
import qs from 'qs'
import { usePostHogParam } from './hooks/usePostHogContext'
import { MetricType } from './types'
import { useProjectStore } from '@/hooks/use-project-store'
import { zipWith } from 'lodash'

export interface RevenueMetricProps {
  activeMetricType: MetricType
  setActiveMetricType: (activeMetricType: MetricType, metricData: any) => void
}

export function RevenueMetric(props: RevenueMetricProps) {
  const { activeMetricType, setActiveMetricType } = props
  const t = useTranslations('analytics')
  const revenueMetrics = ['revenue', 'orders', 'aov']
  const { date_from } = usePostHogParam()

  // 构建基础查询参数
  const baseQueryParams = useMemo(() => {
    const queryObj = {
      startDate: dayjs.utc().subtract(date_from, 'day').toISOString(),
      endDate: dayjs.utc().toISOString(),
    }
    return qs.stringify(queryObj, { arrayFormat: 'repeat' })
  }, [date_from])

  // 生成所有 API URL
  const apiUrls = useMemo(() => {
    const baseUrl = '/api/shopping-service-proxy/api/payment-analytics'
    return {
      revenue: `${baseUrl}/gross-revenue?${baseQueryParams}`,
      orders: `${baseUrl}/orders?${baseQueryParams}`,
    }
  }, [baseQueryParams])
  // 使用 useSWR 获取收入数据
  const { data: revenueData, isLoading: revenueLoading } = useSWR(
    apiUrls.revenue,
    fetcher,
  )

  // 使用 useSWR 获取订单数据
  const { data: ordersData, isLoading: ordersLoading } = useSWR(
    apiUrls.orders,
    fetcher,
  )

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount)
  }

  // 计算 AOV 数据
  const aovData = useMemo(() => {
    if (!revenueData || !ordersData) return null

    const calculateAOV = (revenueTotal: number, ordersTotal: number) => {
      return ordersTotal === 0
        ? 0
        : parseFloat((revenueTotal / ordersTotal).toFixed(2))
    }

    const calculateAOVCurve = (revenueCurve: any[], ordersCurve: any[]) => {
      if (!revenueCurve || !ordersCurve) return []

      return revenueCurve.map((revenueItem, index) => {
        const ordersItem = ordersCurve[index]
        if (!ordersItem || ordersItem.value === 0) {
          return { ...revenueItem, value: 0 }
        }
        return {
          ...revenueItem,
          value: parseFloat((revenueItem.value / ordersItem.value).toFixed(2)),
        }
      })
    }

    return {
      total: formatCurrency(
        calculateAOV(revenueData.total || 0, ordersData.total || 0),
      ),
      curve: calculateAOVCurve(revenueData.curve, ordersData.curve),
    }
  }, [revenueData, ordersData])

  // 构建收入数据映射对象
  const revenueDataMap = useMemo(() => {
    return {
      revenue: {
        data: {
          ...revenueData,
          total: formatCurrency(revenueData?.total || 0),
        },
        loading: revenueLoading,
      },
      orders: {
        data: ordersData,
        loading: ordersLoading,
      },
      aov: {
        data: aovData || [],
        loading: revenueLoading || ordersLoading,
      },
    }
  }, [revenueData, revenueLoading, ordersData, ordersLoading, aovData])

  return revenueMetrics.map((key) => {
    const metricData = revenueDataMap[key as keyof typeof revenueDataMap]
    const { data, loading } = metricData

    return (
      <CoreMetricItem
        key={key}
        title={t(key as MetricType)}
        className={cn('h-28 min-w-0', {
          'bg-white/20 border-white/30': activeMetricType === key,
        })}
        onClick={() => {
          setActiveMetricType(key as MetricType, data?.curve)
        }}
      >
        <div className="text-xl md:text-2xl truncate">
          {loading ? (
            <div className="flex items-center justify-center">
              <div className="relative w-8 h-8">
                <svg
                  className="w-full h-full text-gold/70"
                  viewBox="0 0 100 100"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10 80 L25 60 L40 70 L55 40 L70 50 L85 30 L90 20"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                    strokeDasharray="200"
                    style={{
                      strokeDashoffset: 200,
                      animation: 'drawLine 2s ease-in-out infinite',
                    }}
                  />
                  <circle
                    cx="10"
                    cy="80"
                    r="3"
                    fill="currentColor"
                    className="opacity-60 animate-pulse"
                  />
                  <circle
                    cx="25"
                    cy="60"
                    r="3"
                    fill="currentColor"
                    className="opacity-60 animate-pulse"
                    style={{ animationDelay: '0.2s' }}
                  />
                  <circle
                    cx="40"
                    cy="70"
                    r="3"
                    fill="currentColor"
                    className="opacity-60 animate-pulse"
                    style={{ animationDelay: '0.4s' }}
                  />
                  <circle
                    cx="55"
                    cy="40"
                    r="3"
                    fill="currentColor"
                    className="opacity-60 animate-pulse"
                    style={{ animationDelay: '0.6s' }}
                  />
                  <circle
                    cx="70"
                    cy="50"
                    r="3"
                    fill="currentColor"
                    className="opacity-60 animate-pulse"
                    style={{ animationDelay: '0.8s' }}
                  />
                  <circle
                    cx="85"
                    cy="30"
                    r="3"
                    fill="currentColor"
                    className="opacity-60 animate-pulse"
                    style={{ animationDelay: '1s' }}
                  />
                  <circle
                    cx="90"
                    cy="20"
                    r="3"
                    fill="currentColor"
                    className="opacity-60 animate-pulse"
                    style={{ animationDelay: '1.2s' }}
                  />
                </svg>
              </div>
            </div>
          ) : (
            data?.total || 0
          )}
        </div>
      </CoreMetricItem>
    )
  })
}

export interface ReportMetricProps {
  activeMetricType: MetricType
  setActiveMetricType: (activeMetricType: MetricType, metricData: any) => void
}
export function ReportMetric(props: ReportMetricProps) {
  const { activeMetricType, setActiveMetricType } = props
  const { project } = useProjectStore()
  const t = useTranslations('analytics')

  // 定义指标列表，key 和 eventName 保持一致
  const reportMetrics = ['AddToCart', 'Purchase', 'InitiateCheckout']

  const { date_from } = usePostHogParam()

  const getInsightsUrl = useCallback(
    (event_name: string) => {
      const queryObj = {
        date_from: dayjs.utc().subtract(date_from, 'day').toISOString(),
        aggregation: ['total'],
        event_name: event_name,
      }
      const queryString = qs.stringify(queryObj, { arrayFormat: 'repeat' })

      return `/api/stats/projects/${project?._id.toString()}/insights?${queryString}`
    },
    [project, date_from],
  )

  // 生成所有 API URL
  const apiUrls = useMemo(() => {
    return reportMetrics.reduce(
      (acc, metric) => {
        acc[metric] = getInsightsUrl(metric)
        return acc
      },
      {} as Record<string, string>,
    )
  }, [getInsightsUrl])

  // 获取各个指标的数据
  const { data: addToCartData, isLoading: addToCartLoading } = useSWR(
    apiUrls.AddToCart,
    fetcher,
  )

  const { data: purchaseData, isLoading: purchaseLoading } = useSWR(
    apiUrls.Purchase,
    fetcher,
  )

  const { data: initiateCheckoutData, isLoading: initiateCheckoutLoading } =
    useSWR(apiUrls.InitiateCheckout, fetcher)

  const buildChartData = useCallback((labels: string[], data: number[]) => {
    return zipWith(labels, data, (label, value) => ({
      date: label,
      value: value,
    }))
  }, [])

  const getInsightsData = useCallback(
    (insights: any) => {
      if (!insights?.data) return undefined
      const insightData = insights.data
      const result = insightData?.[0]?.result?.[0] || {}

      const {
        count,
        action: { days },
        data,
      } = result

      const labels = days.map((d: string) => {
        return dayjs.utc(d).local().format('DD-MM')
      })

      return {
        total: count,
        datasets: buildChartData(labels, data),
      }
    },
    [buildChartData],
  )

  // 构建数据映射
  const metricsDataMap = useMemo(() => {
    return {
      AddToCart: {
        data: getInsightsData(addToCartData),
        loading: addToCartLoading,
      },
      Purchase: {
        data: getInsightsData(purchaseData),
        loading: purchaseLoading,
      },
      InitiateCheckout: {
        data: getInsightsData(initiateCheckoutData),
        loading: initiateCheckoutLoading,
      },
    }
  }, [
    addToCartData,
    addToCartLoading,
    purchaseData,
    purchaseLoading,
    initiateCheckoutData,
    initiateCheckoutLoading,
    getInsightsData,
  ])

  return reportMetrics.map((metric) => {
    const metricData = metricsDataMap[metric as keyof typeof metricsDataMap]
    const { data, loading } = metricData || {}

    return (
      <CoreMetricItem
        key={metric}
        title={t(metric as MetricType)}
        className={cn('h-28 min-w-0', {
          'bg-white/20 border-white/30': activeMetricType === metric,
        })}
        onClick={() => {
          setActiveMetricType(metric as MetricType, data?.datasets)
        }}
      >
        <div className="text-xl md:text-2xl truncate">
          {loading ? (
            <div className="flex items-center justify-center">
              <div className="relative w-8 h-8">
                <svg
                  className="w-full h-full text-gold/70"
                  viewBox="0 0 100 100"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10 80 L25 60 L40 70 L55 40 L70 50 L85 30 L90 20"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                    strokeDasharray="200"
                    style={{
                      strokeDashoffset: 200,
                      animation: 'drawLine 2s ease-in-out infinite',
                    }}
                  />
                  <circle
                    cx="10"
                    cy="80"
                    r="3"
                    fill="currentColor"
                    className="opacity-60 animate-pulse"
                  />
                  <circle
                    cx="25"
                    cy="60"
                    r="3"
                    fill="currentColor"
                    className="opacity-60 animate-pulse"
                    style={{ animationDelay: '0.2s' }}
                  />
                  <circle
                    cx="40"
                    cy="70"
                    r="3"
                    fill="currentColor"
                    className="opacity-60 animate-pulse"
                    style={{ animationDelay: '0.4s' }}
                  />
                  <circle
                    cx="55"
                    cy="40"
                    r="3"
                    fill="currentColor"
                    className="opacity-60 animate-pulse"
                    style={{ animationDelay: '0.6s' }}
                  />
                  <circle
                    cx="70"
                    cy="50"
                    r="3"
                    fill="currentColor"
                    className="opacity-60 animate-pulse"
                    style={{ animationDelay: '0.8s' }}
                  />
                  <circle
                    cx="85"
                    cy="30"
                    r="3"
                    fill="currentColor"
                    className="opacity-60 animate-pulse"
                    style={{ animationDelay: '1s' }}
                  />
                  <circle
                    cx="90"
                    cy="20"
                    r="3"
                    fill="currentColor"
                    className="opacity-60 animate-pulse"
                    style={{ animationDelay: '1.2s' }}
                  />
                </svg>
              </div>
            </div>
          ) : (
            data?.total || 0
          )}
        </div>
      </CoreMetricItem>
    )
  })
}
